import React from 'react'
import Ripple from '@/components/Ripple'
import { Loading } from 'react-vant'
interface ButtonProps {
  /** 按钮文本 */
  children: React.ReactNode;
  /** 点击事件 */
  onClick?: () => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否显示加载状态 */
  loading?: boolean;
  /** 按钮颜色类型 */
  color?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'black' | 'outline';
  /** 按钮大小 */
  size?: 'small' | 'medium' | 'large';
  /** 自定义类名 */
  className?: string;
  /** 按钮类型 */
  type?: 'button' | 'submit' | 'reset';
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  disabled = false,
  loading = false,
  color = 'primary',
  size = 'medium',
  className = '',
  type = 'button',
}) => {
  // 处理点击事件
  const handleClick = () => {
    if (disabled || loading) return
    if (onClick) {
      onClick()
    }
  }

  // 获取颜色样式
  const getColorClasses = () => {
    if (disabled || loading) {
      return 'bg-gray-300 text-gray-500 cursor-not-allowed';
    }

    switch (color) {
      case 'default':
        return 'bg-[#eee] text-gray-800 border border-gray-300 active:bg-[#ddd]';
      case 'primary':
        return `bg-[var(--primary-color)] !text-white active:bg-blue-700`;
      case 'success':
        return 'bg-green-500 !text-white active:bg-green-700';
      case 'warning':
        return 'bg-yellow-500 !text-white active:bg-yellow-700';
      case 'danger':
        return 'bg-red-500 !text-white active:bg-red-700';
      case 'black':
        return 'bg-black !text-white active:bg-gray-800';
      case 'outline':
        return 'bg-white text-[var(--primary-color)] border border-solid border-[var(--primary-color)] active:bg-gray-50';
      default:
        return `bg-[var(--primary-color)] !text-white active:bg-[var(--primary-color)]/80`;
    }
  };

  // 获取尺寸样式
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'py-1 px-4 text-sm h-8 !w-auto';
      case 'medium':
        return 'py-3 px-6 text-base h-12';
      case 'large':
        return 'py-4 px-8 text-lg h-14';
      default:
        return 'py-3 px-6 text-base h-12';
    }
  };

  // 根据颜色获取 ripple 颜色
  const getRippleColor = () => {
    if (disabled || loading) return 'rgba(0, 0, 0, 0.4)'

    switch (color) {
      case 'default':
      case 'primary':
      case 'success':
      case 'warning':
      case 'outline':
        return 'rgba(0, 0, 0, 0.4)'
      case 'danger':
      case 'black':
        return 'rgba(255, 255, 255, 0.4)'
      default:
        return 'rgba(0, 0, 0, 0.4)'
    }
  }

  return (
    <Ripple
      color={getRippleColor()}
      disabled={disabled || loading}
      className="rounded-full w-full"
    >
      <button
        type={type}
        onClick={handleClick}
        disabled={disabled || loading}
        className={`
          relative
          w-full rounded-full font-medium
          transition-all duration-200 ease-in-out
          outline-none
          ${getColorClasses()}
          ${getSizeClasses()}
          ${className}
        `}
      >
        {/* 按钮内容 */}
        <span className="relative flex justify-center items-center text-sm">
          {loading && (
            <Loading size="15" color="#6b7280" className="mr-1" />
          )}
          {children}
        </span>
      </button>
    </Ripple>
  )
}

export default Button;